import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";

import logo from "@/assets/logo.png";
import { useAuth } from "@/hooks/useAuth";

/** Human-readable messages for the `?error=` query param surfaced by the OAuth callback. */
const errorMessages: Record<string, string> = {
  not_authorized: "This Google account isn't authorized. Contact your administrator for access.",
  org_blocked: "Your organization blocked sign-in. Contact your administrator.",
};

const GENERIC_ERROR = "Sign-in failed. Please try again.";

function resolveErrorMessage(code: string | null): string | null {
  if (!code) return null;
  return errorMessages[code] ?? GENERIC_ERROR;
}

const Login = () => {
  const { user, loading, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const errorMessage = resolveErrorMessage(searchParams.get("error"));

  useEffect(() => {
    if (!loading && user) {
      void navigate("/", { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground text-sm">Loading...</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-sidebar">
      <div className="flex flex-col items-center gap-6 rounded-2xl bg-white shadow-md max-w-sm w-full overflow-hidden border border-border">
        <div className="w-full flex items-center justify-center py-4 bg-primary">
          <img src={logo} alt="Logo" className="h-14 w-auto" />
        </div>
        <div className="flex flex-col items-center gap-6 px-10 pb-10">
          {errorMessage && (
            <p role="alert" className="text-destructive text-sm text-center">
              {errorMessage}
            </p>
          )}
          {/* Brand exception: Google requires specific button styling */}
          <button
            onClick={signInWithGoogle}
            className="w-full inline-flex items-center justify-center gap-3 px-4 py-2.5 rounded-xl border border-[#dadce0] bg-white text-sm font-medium text-[#3c4043] transition-colors hover:bg-[#f7f8f8] shadow-xs"
          >
            {/* eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- inline brand SVG; <img src=...> would require a separate asset round-trip and lose the live brand-color paths. aria-hidden defers naming to the parent <button>. */}
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" role="img">
              <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
              />
              <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
              />
              <path
                fill="#FBBC05"
                d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.01 24.01 0 0 0 0 21.56l7.98-6.19z"
              />
              <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
              />
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    </main>
  );
};

export default Login;
